/**
 * OnboardingTour — Phase 4.
 *
 * Renders a 6-step `react-joyride` walkthrough the first time an authenticated
 * user reaches the dashboard layout. Triggered when ``user.onboarding_completed``
 * is `false`. When the tour finishes (or is skipped), we PATCH
 * `/users/me/onboarding` so the flag is sticky.
 *
 * Steps target DOM nodes by ``data-testid`` so the tour also doubles as a
 * sanity check that the critical landmarks exist.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Joyride, STATUS } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import users from "@/api/users";

const TOUR_KEY = "muleria_onboarding_seen";

function buildSteps(t) {
  return [
    {
      target: '[data-testid="sidebar"]',
      title: t("onboarding.steps.sidebar.title"),
      content: t("onboarding.steps.sidebar.content"),
      placement: "right",
      disableBeacon: true,
    },
    {
      target: '[data-testid="nav-dashboard"]',
      title: t("onboarding.steps.dashboard.title"),
      content: t("onboarding.steps.dashboard.content"),
      placement: "right",
    },
    {
      target: '[data-testid="nav-upload"]',
      title: t("onboarding.steps.upload.title"),
      content: t("onboarding.steps.upload.content"),
      placement: "right",
    },
    {
      target: '[data-testid="nav-employees-canonical"]',
      title: t("onboarding.steps.employees.title"),
      content: t("onboarding.steps.employees.content"),
      placement: "right",
    },
    {
      target: '[data-testid="nav-share"]',
      title: t("onboarding.steps.share.title"),
      content: t("onboarding.steps.share.content"),
      placement: "right",
    },
    {
      target: '[data-testid="nav-help"]',
      title: t("onboarding.steps.help.title"),
      content: t("onboarding.steps.help.content"),
      placement: "right",
    },
  ];
}

export default function OnboardingTour() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const [run, setRun] = useState(false);

  // `t` itself updates whenever the language changes, so listing `i18n.language`
  // would be redundant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const steps = useMemo(() => buildSteps(t), [t]);

  // Decide when to launch the tour:
  // - Only for authenticated users with onboarding_completed=false.
  // - Plus a localStorage guard so a brief race-condition refresh doesn't
  //   double-prompt before the API patch completes.
  useEffect(() => {
    if (!user || user === false) return;
    if (user.onboarding_completed) return;
    if (localStorage.getItem(TOUR_KEY) === "1") return;
    // Defer to ensure the target elements (NavItems) are mounted.
    const id = setTimeout(() => setRun(true), 600);
    return () => clearTimeout(id);
  }, [user]);

  const handleCallback = useCallback(
    async (data) => {
      const { status, type } = data;
      const finished = [STATUS.FINISHED, STATUS.SKIPPED].includes(status);
      if (finished) {
        setRun(false);
        localStorage.setItem(TOUR_KEY, "1");
        try {
          await users.setOnboardingCompleted(true);
          await refresh();
        } catch (err) {
          // Intentionally swallowed: the local TOUR_KEY flag below still
          // prevents replay even if the persistence call fails.
          console.debug("OnboardingTour: persistence failed (local flag set):", err?.response?.status || err?.message);
        }
      } else if (type === "error:target_not_found") {
        // If a step's target is missing (e.g., user lacks the permission for
        // the Upload link), skip the tour instead of getting stuck.
        setRun(false);
        localStorage.setItem(TOUR_KEY, "1");
        users.setOnboardingCompleted(true).catch((err) => {
          // Intentionally swallowed: local flag already prevents replay.
          console.debug("OnboardingTour: skip-persistence failed:", err?.response?.status || err?.message);
        });
      }
    },
    [refresh]
  );

  // Listen for a global "muleria:restart-tour" event so users can re-trigger
  // the walkthrough from the help center if needed.
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(TOUR_KEY);
      setRun(true);
    };
    window.addEventListener("muleria:restart-tour", handler);
    return () => window.removeEventListener("muleria:restart-tour", handler);
  }, []);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      hideCloseButton={false}
      callback={handleCallback}
      locale={{
        back: t("onboarding.back"),
        close: t("onboarding.close"),
        last: t("onboarding.last"),
        next: t("onboarding.next"),
        skip: t("onboarding.skip"),
        open: t("onboarding.open"),
      }}
      styles={{
        options: {
          primaryColor: "hsl(var(--primary))",
          textColor: "hsl(var(--foreground))",
          backgroundColor: "hsl(var(--card))",
          arrowColor: "hsl(var(--card))",
          overlayColor: "rgba(0,0,0,0.55)",
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
        },
        buttonNext: {
          backgroundColor: "hsl(var(--primary))",
          color: "hsl(var(--primary-foreground))",
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 8,
          padding: "8px 14px",
        },
        buttonBack: {
          color: "hsl(var(--muted-foreground))",
          fontSize: 13,
        },
        buttonSkip: {
          color: "hsl(var(--muted-foreground))",
          fontSize: 12,
        },
      }}
    />
  );
}
